import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import useProfile from './useProfile';
import { palette } from './ui';
import CompletenessCard from './CompletenessCard';
import CategoryPicker from './CategoryPicker';
import BasicInfoCard from './BasicInfoCard';
import PreferencesCard from './PreferencesCard';
import ExperienceCard from './ExperienceCard';
import EducationCard from './EducationCard';
import SkillsCard from './SkillsCard';
import LanguagesCard from './LanguagesCard';
import CertificationsCard from './CertificationsCard';
import ResumeCard from './ResumeCard';
import IdVerificationCard from './IdVerificationCard';

export default function ProfilePage({ isDarkMode, user }) {
  const navigate = useNavigate();
  const c = palette(isDarkMode);
  const {
    profile, experience, education, skills, languages, certifications,
    completeness, filledSections, loading, error, isNew, toast,
    reload, saveProfile, sections, resume,
  } = useProfile();

  const email = (user && user.email) || profile.email;

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-black' : 'bg-paper'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-purple" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center gap-4 px-4 ${isDarkMode ? 'bg-black' : 'bg-paper'}`}>
        <p className={`text-sm ${c.text}`}>{error}</p>
        <button onClick={reload} className={`px-5 py-2.5 rounded-xl text-sm font-semibold ${c.primaryBtn}`}>
          Try again
        </button>
      </div>
    );
  }

  const isBlueCollar = profile.work_category === 'blue_collar';
  const needsCategory = !profile.work_category;

  const cards = {
    basic: <BasicInfoCard key="basic" isDarkMode={isDarkMode} profile={profile} email={email} onSave={saveProfile} />,
    identity: <IdVerificationCard key="identity" isDarkMode={isDarkMode} profile={profile} onSave={saveProfile} />,
    preferences: <PreferencesCard key="preferences" isDarkMode={isDarkMode} profile={profile} onSave={saveProfile} />,
    experience: <ExperienceCard key="experience" isDarkMode={isDarkMode} items={experience} actions={sections.experience} />,
    education: <EducationCard key="education" isDarkMode={isDarkMode} items={education} actions={sections.education} />,
    skills: <SkillsCard key="skills" isDarkMode={isDarkMode} items={skills} actions={sections.skills} />,
    languages: <LanguagesCard key="languages" isDarkMode={isDarkMode} items={languages} actions={sections.languages} />,
    certifications: <CertificationsCard key="certifications" isDarkMode={isDarkMode} items={certifications} actions={sections.certifications} />,
    resume: <ResumeCard key="resume" isDarkMode={isDarkMode} profile={profile} resume={resume} />,
  };

  const order = isBlueCollar
    ? ['basic', 'identity', 'preferences', 'experience', 'skills', 'languages', 'education', 'certifications', 'resume']
    : ['basic', 'preferences', 'experience', 'education', 'skills', 'languages', 'certifications', 'resume', 'identity'];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-black' : 'bg-paper'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-10">
        <button
          onClick={() => navigate('/search')}
          className={`flex items-center gap-2 text-sm mb-4 ${c.secondaryText} hover:text-brand-purple hover:underline`}
        >
          <FaArrowLeft className="h-3.5 w-3.5" /> Back to job search
        </button>

        <div className="mb-6">
          <h1 className={`text-2xl sm:text-3xl font-bold font-display ${c.text}`}>My Profile</h1>
          <p className={`text-sm mt-1 ${c.secondaryText}`}>
            This is what employers see when you apply. Keep it complete to stand out.
          </p>
        </div>

        {needsCategory ? (
          <CategoryPicker isDarkMode={isDarkMode} onPick={(work_category) => saveProfile({ work_category })} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-6 items-start">
            <div className="md:sticky md:top-6">
              <CompletenessCard
                isDarkMode={isDarkMode}
                profile={profile}
                email={email}
                completeness={completeness}
                filledSections={filledSections}
                onJumpTo={jumpTo}
              />
            </div>
            <div className="space-y-5 min-w-0">
              {isNew && (
                <div className={`rounded-2xl border p-4 text-sm flex items-start gap-3 ${isDarkMode ? 'bg-brand-purple/10 border-brand-purple/30' : 'bg-brand-purple/5 border-brand-purple/20'}`}>
                  <span className="text-lg">👋</span>
                  <p className={c.text}>
                    <span className="font-semibold">Welcome!</span> Fill in your details below — employers see this profile when you apply.
                  </p>
                </div>
              )}
              {order.map(id => cards[id])}
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-full shadow-lg text-sm font-medium
            ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
